import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";

import { getTenantCurrent } from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";
import {
  readDgiStepsCompletedFromSession,
  writeDgiStepsCompletedToSession,
  writeDgiStep3ContinuedToSession,
} from "@/pages/onboarding/dgi-onboarding-session";
import {
  readDgiUserConfirmedToastShownFromSession,
  writeDgiUserConfirmedToastShownToSession,
} from "@/pages/onboarding/onboardingSessionState";

// last_numbers intentionally absent — the step no longer runs, so requiring it
// here would make hasAllAutoFlowStepsComplete permanently false.
const AUTO_FLOW_STEP_KEYS = [
  "portal_user",
  "company_info",
  "cae_download",
  "proveedor_software",
] as const;

function hasAllAutoFlowStepsComplete(
  progress: Record<string, string> | null | undefined,
  situationChoice?: SituationChoice | null,
): boolean {
  if (!progress) return false;
  // Scratch tenants skip proveedor_software (not yet a DGI-homologated
  // proveedor for them) — accept a 4-key chain as fully complete.
  return AUTO_FLOW_STEP_KEYS.every((k) => {
    if (k === "proveedor_software" && situationChoice === "scratch") {
      return true;
    }
    return Boolean(progress[k]);
  });
}

const DGI_CONFIRM_POLL_INTERVAL_MS = 30_000;

interface UseDgiConfirmationParams {
  tenant: TenantCurrentResponse | null;
  loadTenant: () => Promise<void>;
  viewStep: number;
  /** Resolved DGI-access viewStep (3 + preCertOffset). */
  dgiStep: number;
  /** Resolved cert-choice / abitab / CAE viewSteps for the DGI exit routing. */
  certChoiceStep: number;
  abitabStep: number;
  caeStep: number;
  t: TFunction;
  situationChoice: SituationChoice | null;
  certificatePath: "manual" | "fiscalo" | null;
  setCertificatePath: Dispatch<SetStateAction<"manual" | "fiscalo" | null>>;
  setViewStep: Dispatch<SetStateAction<number>>;
  setViewStepSynced: Dispatch<SetStateAction<boolean>>;
  setCertificateAcquisitionChoice: Dispatch<
    SetStateAction<"myself" | "fiscalo" | null>
  >;
}

/**
 * Owns everything about the DGI portal-user confirmation on step 3: the
 * "steps done" checkbox state (persisted), the success-toast acknowledgment
 * gate, whether the initial confirmation status has resolved, and the 30s poll
 * that waits for DGI to confirm the portal user. The step machine reads
 * `dgiStepsCompletedConfirmed` / `dgiStep3SuccessToastAcknowledged` to decide
 * whether step 3 is complete, and calls the returned setters from
 * handleContinueFromDgi.
 */
export function useDgiConfirmation({
  tenant,
  loadTenant,
  viewStep,
  dgiStep,
  certChoiceStep,
  abitabStep,
  caeStep,
  t,
  situationChoice,
  certificatePath,
  setCertificatePath,
  setViewStep,
  setViewStepSynced,
  setCertificateAcquisitionChoice,
}: UseDgiConfirmationParams) {
  const [dgiPasswordReady, setDgiPasswordReady] = useState(false);
  const [dgiStepsCompletedConfirmed, setDgiStepsCompletedConfirmedRaw] =
    useState(false);
  /** Step 3: Next stays disabled until success toast has been shown (after DGI confirms the portal user). */
  const [dgiStep3SuccessToastAcknowledged, setDgiStep3SuccessToastAcknowledged] =
    useState(false);
  /** After first load-time DGI check: avoids showing "Esperando DGI" until /tenants/current is verified. */
  const [dgiConfirmationStatusResolved, setDgiConfirmationStatusResolved] =
    useState(false);
  const dgiConfirmPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  /** Avoid duplicate "DGI confirmed" toasts for the same tenant in one session. */
  const dgiUserConfirmedToastForTenantRef = useRef<string | null>(null);

  useEffect(() => {
    if (viewStep !== dgiStep) {
      setDgiPasswordReady(false);
      setDgiStep3SuccessToastAcknowledged(false);
    }
  }, [viewStep, dgiStep]);

  // Restore dgiStepsCompletedConfirmed from sessionStorage OR from tenant's
  // dgiAutoOnboardingProgress (when all 5 auto-flow steps have a timestamp).
  // The DB-derived branch is authoritative across tab close — without it, a
  // refresh after the auto-flow completes would treat the step as incomplete
  // and re-run the orchestration.
  useEffect(() => {
    if (!tenant?.id) return;
    if (readDgiStepsCompletedFromSession(tenant.id)) {
      setDgiStepsCompletedConfirmedRaw(true);
      return;
    }
    if (
      hasAllAutoFlowStepsComplete(
        tenant.dgiAutoOnboardingProgress as
          | Record<string, string>
          | null
          | undefined,
        tenant.onboardingSituationChoice,
      )
    ) {
      setDgiStepsCompletedConfirmedRaw(true);
    }
  }, [tenant?.id, tenant?.dgiAutoOnboardingProgress]);

  const setDgiStepsCompletedConfirmed = useCallback(
    (value: boolean) => {
      setDgiStepsCompletedConfirmedRaw(value);
      if (tenant?.id) {
        writeDgiStepsCompletedToSession(tenant.id, value);
      }
    },
    [tenant?.id],
  );

  useEffect(() => {
    if (!tenant?.id) {
      setDgiConfirmationStatusResolved(false);
      return;
    }
    const hasConfirmedOnTenant =
      Boolean(tenant.dgiPortalUserConfirmed) ||
      Boolean(tenant.dgiPortalUserConfirmedAt);
    if (!tenant.hasDgiPortalUser || hasConfirmedOnTenant) {
      setDgiConfirmationStatusResolved(true);
      return;
    }
    let cancelled = false;
    void getTenantCurrent()
      .then((fresh) => {
        if (cancelled) return;
        const confirmed =
          Boolean(fresh.dgiPortalUserConfirmed) ||
          Boolean(fresh.dgiPortalUserConfirmedAt);
        if (confirmed) {
          void loadTenant();
        }
      })
      .catch(() => {
        /* keep cached tenant; still allow banner logic */
      })
      .finally(() => {
        if (!cancelled) setDgiConfirmationStatusResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    tenant?.id,
    tenant?.hasDgiPortalUser,
    tenant?.dgiPortalUserConfirmed,
    tenant?.dgiPortalUserConfirmedAt,
    loadTenant,
  ]);

  useEffect(() => {
    dgiUserConfirmedToastForTenantRef.current = null;
    setDgiStep3SuccessToastAcknowledged(false);
  }, [tenant?.id]);

  /**
   * Single place for DGI user confirmed success toast on step 3; gates Next until toast is shown.
   * Poll only refreshes tenant; this effect runs after tenant reflects confirmation.
   *
   * The "already shown" guard is persisted to sessionStorage so a hard
   * page refresh on viewStep=3 doesn't re-fire the toast. (An in-memory
   * useRef resets on remount.)
   */
  useEffect(() => {
    if (viewStep !== dgiStep || !tenant?.id) return;
    const dgiConfirmed =
      Boolean(tenant.dgiPortalUserConfirmed) ||
      Boolean(tenant.dgiPortalUserConfirmedAt);
    if (!tenant.hasDgiPortalUser || !dgiConfirmed) {
      setDgiStep3SuccessToastAcknowledged(false);
      return;
    }
    const alreadyShown =
      dgiUserConfirmedToastForTenantRef.current === tenant.id ||
      readDgiUserConfirmedToastShownFromSession(tenant.id);
    if (alreadyShown) {
      dgiUserConfirmedToastForTenantRef.current = tenant.id;
      setDgiStep3SuccessToastAcknowledged(true);
      return;
    }
    toast.success(t("onboarding.dgiUserConfirmedToast"));
    dgiUserConfirmedToastForTenantRef.current = tenant.id;
    writeDgiUserConfirmedToastShownToSession(tenant.id);
    setDgiStep3SuccessToastAcknowledged(true);
  }, [
    viewStep,
    dgiStep,
    tenant?.id,
    tenant?.hasDgiPortalUser,
    tenant?.dgiPortalUserConfirmed,
    tenant?.dgiPortalUserConfirmedAt,
    t,
  ]);

  useEffect(() => {
    const dgiConfirmed =
      Boolean(tenant?.dgiPortalUserConfirmed) ||
      Boolean(tenant?.dgiPortalUserConfirmedAt);
    if (!tenant?.hasDgiPortalUser || dgiConfirmed) {
      if (dgiConfirmPollIntervalRef.current) {
        clearInterval(dgiConfirmPollIntervalRef.current);
        dgiConfirmPollIntervalRef.current = null;
      }
      return;
    }

    /** Poll even on step 3 before the "steps done" checkbox: DGI may confirm first; banner stays gated in onboarding-page. */
    const runDgiConfirmationCheck = (): void => {
      void getTenantCurrent().then((res) => {
        const confirmed =
          Boolean(res.dgiPortalUserConfirmed) ||
          Boolean(res.dgiPortalUserConfirmedAt);
        if (confirmed) {
          if (dgiConfirmPollIntervalRef.current) {
            clearInterval(dgiConfirmPollIntervalRef.current);
            dgiConfirmPollIntervalRef.current = null;
          }
          void loadTenant();
        }
      });
    };

    runDgiConfirmationCheck();
    const id = setInterval(runDgiConfirmationCheck, DGI_CONFIRM_POLL_INTERVAL_MS);
    dgiConfirmPollIntervalRef.current = id;
    return () => {
      if (dgiConfirmPollIntervalRef.current) {
        clearInterval(dgiConfirmPollIntervalRef.current);
        dgiConfirmPollIntervalRef.current = null;
      }
    };
  }, [
    tenant?.hasDgiPortalUser,
    tenant?.dgiPortalUserConfirmed,
    tenant?.dgiPortalUserConfirmedAt,
    loadTenant,
    t,
  ]);

  /**
   * Step-3 exit ("Seguir con el certificado"): re-read the tenant, decide
   * whether DGI is settled enough to leave (accepting progress.portal_user and,
   * for non-scratch tenants, the manual "ya lo hice" self-attestation), persist
   * the "continued" marker, then route to the next step for the situation path.
   */
  const handleContinueFromDgi = useCallback(async () => {
    await loadTenant();
    const fresh = await getTenantCurrent();
    const progress = (fresh.dgiAutoOnboardingProgress ?? {}) as Record<
      string,
      unknown
    >;
    const portalUserStamped = Boolean(progress.portal_user);
    const dgiConfirmed =
      Boolean(fresh.dgiPortalUserConfirmed) ||
      Boolean(fresh.dgiPortalUserConfirmedAt) ||
      portalUserStamped;
    // Scratch tenants MUST have the e-Factura user actually created/confirmed
    // before leaving step 3 (no pre-existing portal user to self-attest against);
    // for issuer/already-issuer the manual self-attestation still stands.
    const isScratchTenant =
      fresh.onboardingSituationChoice === "scratch" ||
      situationChoice === "scratch";
    if (!dgiConfirmed && (isScratchTenant || !dgiStepsCompletedConfirmed)) {
      // Force re-sync so the step effect picks up the latest tenant state.
      setViewStepSynced(false);
      return;
    }
    setDgiStep3SuccessToastAcknowledged(true);
    setDgiStepsCompletedConfirmed(true);
    if (fresh.id) {
      writeDgiStep3ContinuedToSession(fresh.id);
    }
    // Regularization (cert-first): the cert request was already created before
    // DGI auto-flow, so skip the cert steps and advance to CAE. scratch is
    // DGI-first — the cert has NOT been created yet, so it must fall through to
    // the cert-choice step below (routing it to CAE here is the bug that skips
    // the certificate step).
    const certWasDoneBeforeDgi =
      fresh.onboardingSituationChoice === "issuer_need_regularization" ||
      situationChoice === "issuer_need_regularization";
    // No already_issuer branch here. It used to force certificatePath="manual"
    // and jump to abitabStep — the same "a migrating tenant already has a
    // certificate" assumption that resolveStep and useOnboarding both carried,
    // and that spec cc0cd3d9 removed from them. Leaving it here would have
    // re-opened the cert_choice <-> abitab loop fixed in b2b24ab0, and once the
    // path became durable it would have survived a refresh rather than a tab.
    // already_issuer now falls through to cert_choice like everyone else.
    if (certWasDoneBeforeDgi) {
      setViewStep(caeStep);
    } else {
      setViewStep(certChoiceStep);
    }
  }, [
    loadTenant,
    setDgiStepsCompletedConfirmed,
    situationChoice,
    certificatePath,
    setCertificatePath,
    dgiStepsCompletedConfirmed,
    setViewStep,
    setViewStepSynced,
    setCertificateAcquisitionChoice,
    abitabStep,
    caeStep,
    certChoiceStep,
  ]);

  return {
    dgiPasswordReady,
    setDgiPasswordReady,
    dgiStepsCompletedConfirmed,
    setDgiStepsCompletedConfirmed,
    dgiStep3SuccessToastAcknowledged,
    setDgiStep3SuccessToastAcknowledged,
    dgiConfirmationStatusResolved,
    handleContinueFromDgi,
  };
}
